"use client";

// Slim top-of-page progress bar that shows while a client-side navigation is
// in flight. App Router doesn't expose router transition events (Pages Router's
// `routeChangeStart`/`routeChangeComplete` are gone), so we intercept internal
// <a> clicks manually and hide the bar when `usePathname` reports the new URL.
//
// Why this exists: our /blog, /case-studies, /services pages render dynamically
// (see comments in src/app/blog/page.tsx) and a Mongo round-trip on Amplify
// takes 1–2s. Without this bar, clicks feel dead — the browser sits on the
// current page until the server responds. This gives immediate feedback.

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

export default function RouteLoadingBar() {
  const pathname = usePathname();
  const [loading, setLoading] = useState(false);

  // Any pathname change means the navigation completed — hide the bar.
  useEffect(() => {
    setLoading(false);
  }, [pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // Ignore modified clicks (open-in-new-tab, download, etc.).
      // NOTE: we deliberately do NOT check e.defaultPrevented — Next.js <Link>
      // calls preventDefault() to hijack navigation, so defaultPrevented is
      // true for exactly the clicks we care about.
      if (e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;
      // Skip external links, hash links, mailto/tel, downloads, new-tab.
      if (
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }
      if (/^https?:\/\//i.test(href)) {
        try {
          const url = new URL(href);
          if (url.origin !== window.location.origin) return;
        } catch {
          return;
        }
      }

      // Same-URL clicks don't trigger a route change, so the effect above
      // wouldn't hide the bar — bail out.
      try {
        const url = new URL(href, window.location.href);
        if (
          url.pathname === window.location.pathname &&
          url.search === window.location.search
        ) {
          return;
        }
      } catch {
        return;
      }

      setLoading(true);
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  if (!loading) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        pointerEvents: "none",
        overflow: "hidden",
        background: "rgba(66, 94, 249, 0.15)",
      }}
    >
      <div
        style={{
          width: "40%",
          height: "100%",
          background: "#425ef9",
          animation: "route-loading-bar 900ms cubic-bezier(0.4, 0, 0.2, 1) infinite",
        }}
      />
      <style>{`
        @keyframes route-loading-bar {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
    </div>
  );
}
